import { useEffect, useState } from "react";
import { differenceInCalendarDays, format, parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { Progress } from "@/components/ui/progress";
// MS601 (Phase 6 observability): the budget queries throw on RLS /
// schema errors, but the widget swallows that completely — a period
// error makes `period` null so the widget just hides, and a totals
// error falls back to zeroed stats. Either way nothing reached Sentry,
// so a budget-schema regression failed silently. Capture the error
// object + `scope: "dashboard_budget"` ONLY (the errors are Postgres
// objects — codes/hints, no company/financial PII); the hide / zero
// fallback behaviour is unchanged.
import { Sentry } from "@/lib/sentry";
import {
  useCurrentBudgetPeriod,
  useBudgetSpentInPeriod,
  useBurnRateForecast,
  type BurnRateForecast,
  type CompanySettings,
} from "@/hooks/queries";
import { BudgetAdjustmentModal } from "./BudgetAdjustmentModal";

/**
 * MS556 + MS558: Dashboard pool-status widget with burn-rate warning.
 *
 * Renders a compact card on /dashboard showing the active period's
 * spent / scheduled / remaining numbers + a progress bar. Hidden
 * entirely when:
 *   1. pool_cadence === 'per_celebration' (no shared pool by design)
 *   2. pool_cadence === null            (unconfigured company — widget
 *                                        appears after MS554's panel
 *                                        writes the cadence)
 *   3. no active period row in DB       (per-celebration cadence in
 *                                        flight, or company hasn't
 *                                        lazy-created its first period
 *                                        yet)
 *
 * MS558 adds a burn-rate forecast row beneath the four stats. Severity
 * tiers — 'none' (silent), 'caution' (subtle caption), 'warning'
 * (butter banner), 'critical' (coral banner). The banner always
 * surfaces a Settings link so the buyer can act; submit/checkout flows
 * elsewhere stay enabled regardless of level — warn but never block,
 * per MS552 §6 product lock.
 *
 * What this widget intentionally does NOT do:
 *   - Inline burn-rate inside the celebration creation form. Deferred
 *     to MS552-followup-A (Option B; MS558 is dashboard-only).
 *   - Top-up button / activity drawer. Those depend on MS557's
 *     adjustment modal entry point; this widget stays read-only.
 *   - Cadence transition badge (next-cadence preview). MS559's flow.
 *
 * Visual treatment matches Dashboard.tsx's existing cards: white bg,
 * navy text, JetBrains Mono micro-labels, coral accent for progress
 * fill. Sits at the top of the dashboard main column, above the
 * AutoPilot warning + "Up next" headline, so the buyer's first scan
 * is "where am I on this period's spend."
 */

interface DashboardPoolWidgetProps {
  /** The company's pool_cadence from useCompanySettings. The widget
   *  hides itself when this is `per_celebration` or null — the
   *  Dashboard caller still passes it through unconditionally so the
   *  widget owns the hide-logic in one place. */
  poolCadence: CompanySettings["pool_cadence"] | undefined;
  /** Company ID — required for both the period query and the totals
   *  query. Widget hides when null (anonymous / demo context). */
  companyId: string | null;
  /** MS557: actor's profile id for the created_by column on inserted
   *  audit rows. Optional — when null, the column is left null too
   *  (schema allows it via ON DELETE SET NULL). Dashboard.tsx threads
   *  this through from useProfile() so the widget doesn't take a hard
   *  dependency on AuthProvider; the pool-widget tests mount the
   *  component without AuthProvider and would crash if the widget
   *  called useProfile() itself. */
  createdBy?: string | null;
}

export function DashboardPoolWidget({
  poolCadence,
  companyId,
  createdBy = null,
}: DashboardPoolWidgetProps) {
  // MS557: adjustment modal open state. Lives in the widget so the
  // "Adjust" link below the stats can toggle it without dragging
  // Dashboard.tsx into the budget contract. Settings → Budget owns
  // its own copy of this state for its own entry point.
  const [adjustOpen, setAdjustOpen] = useState(false);

  // Hide guard #1: cadence says no pool. Cheaper than running the
  // period query first — if the company has explicitly chosen
  // per-celebration billing, the entire downstream stack is wasted.
  // null also hides because the company hasn't configured a cadence
  // yet (Settings → Budget will write a non-null value on first save).
  const shouldRender =
    poolCadence === "weekly" ||
    poolCadence === "monthly" ||
    poolCadence === "quarterly";

  const periodQuery = useCurrentBudgetPeriod(shouldRender ? companyId : null);
  const period = periodQuery.data ?? null;

  const totalsQuery = useBudgetSpentInPeriod(
    companyId,
    period?.id ?? null,
    period?.period_start ?? null,
    period?.period_end ?? null,
  );

  // MS558: burn-rate forecast. Reuses the same period + totals queries
  // from above via the queryKey cache — the hook does NOT re-fetch.
  // The forecast is pure computation against already-loaded data, so
  // it lands in the same render that paints the stats. Returns a typed
  // forecast with warningLevel='none' when inputs aren't ready.
  const forecast = useBurnRateForecast(shouldRender ? companyId : null);

  // MS601: page Sentry when either budget query errors. Without this the
  // period error hides the widget (via `!period` below) and the totals
  // error zeroes the stats — both silent. useEffect-on-isError mirrors
  // the MS526 idiom; React Query holds the error reference stable so it
  // fires ~once per failure. Captures the error + scope tag ONLY.
  useEffect(() => {
    const err = periodQuery.error ?? totalsQuery.error;
    if ((periodQuery.isError || totalsQuery.isError) && err) {
      Sentry.captureException(err, {
        tags: { scope: "dashboard_budget" },
      });
    }
  }, [
    periodQuery.isError,
    periodQuery.error,
    totalsQuery.isError,
    totalsQuery.error,
  ]);

  // Hide guard #2: cadence is pooled but no active period row exists
  // (degenerate state — MS553's lazy-create should fill this gap after
  // the buyer configures budget in MS554, but a brand-new monthly
  // company between cadence-save and first period creation lands here
  // briefly). Show nothing rather than a $0/$0 stub — degenerate widgets
  // are worse than absent ones per MS552 §4.
  if (!shouldRender) return null;
  if (periodQuery.isLoading) {
    // Render the loading skeleton in-place so the dashboard doesn't
    // jump when the row resolves. Same heuristic as ListLoading on
    // Dashboard.tsx — pulse strip the same height as the populated card.
    return <PoolWidgetSkeleton />;
  }
  if (!period) return null;

  // Composition: numerator counts everything booked against the pool
  // (already-paid + still-in-flight). Denominator is the period's
  // capacity. Both expressed in cents so the percent maths stays
  // integer.
  const totals = totalsQuery.data ?? {
    spentCents: 0,
    scheduledCents: 0,
    adjustmentsCents: 0,
  };
  const capacityCents =
    period.allocated_cents +
    period.carried_over_cents +
    totals.adjustmentsCents;
  const remainingCents = capacityCents - totals.spentCents;
  // Progress bar fills against `spent / capacity`. Scheduled is
  // surfaced as a separate number rather than baked into the fill —
  // double-counting scheduled-as-spent would mislead the buyer (the
  // pending order may still get cancelled). Clamp 0–100 so a
  // mid-period adjustment that reduces capacity below current spend
  // doesn't shoot past 100% visually.
  const progressPercent = (() => {
    if (capacityCents <= 0) return 0;
    const raw = (totals.spentCents / capacityCents) * 100;
    if (raw < 0) return 0;
    if (raw > 100) return 100;
    return Math.round(raw);
  })();

  return (
    <div
      className="mx-8 mb-4 rounded-lg border bg-white p-5"
      style={{ borderColor: "rgba(29,52,97,0.08)" }}
      data-testid="dashboard-pool-widget"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div
            className="text-[10.5px] font-medium uppercase"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              letterSpacing: "0.06em",
              color: "rgba(29,52,97,0.55)",
            }}
          >
            {periodLabel(period.cadence_type, period.period_start)}
          </div>
          <div
            className="mt-0.5 text-[12.5px]"
            style={{ color: "rgba(29,52,97,0.55)" }}
          >
            {periodEndsLabel(period.period_end)}
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-[10px] font-medium uppercase"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              letterSpacing: "0.06em",
              color: "rgba(29,52,97,0.35)",
            }}
          >
            Paid so far
          </div>
          <div
            className="text-[16px] font-semibold tabular-nums"
            style={{ color: "rgba(29,52,97,0.92)" }}
          >
            {progressPercent}%
          </div>
        </div>
      </div>

      <Progress
        value={progressPercent}
        className="mb-2 h-1.5"
      />

      {/* The bar tracks paid spend only; scheduled orders are still in
          flight (and may be cancelled), so they sit in their own stat
          below rather than inflating the fill. This caption keeps the
          buyer from reading the bar as their true remaining headroom. */}
      <div
        className="mb-4 text-[11px] leading-[1.4]"
        style={{ color: "rgba(29,52,97,0.45)" }}
      >
        Scheduled orders are tracked separately and not yet counted here.
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Spent" cents={totals.spentCents} />
        <Stat
          label="Remaining"
          cents={remainingCents}
          tone={remainingCents < 0 ? "warn" : "default"}
        />
        <Stat label="Scheduled" cents={totals.scheduledCents} />
      </div>

      <BurnRateWarning forecast={forecast} />

      {/* MS557: adjustment entry-point. Rendered as a tertiary link in
          the widget footer so it doesn't compete with the stats.
          Implemented as a <button> (modal trigger) styled as a link;
          the existing MS558 "no <button>" assertion in
          dashboard-pool-widget.test.tsx is updated to scope the
          warn-but-never-block lock to the BurnRateWarning subtree
          rather than the whole widget. */}
      <div
        className="mt-3 flex justify-end"
        data-testid="dashboard-pool-widget-adjust-row"
      >
        <button
          type="button"
          onClick={() => setAdjustOpen(true)}
          className="text-[12px] underline underline-offset-2"
          style={{ color: "rgba(29,52,97,0.65)" }}
          data-testid="dashboard-pool-widget-adjust-link"
        >
          Adjust this period
        </button>
      </div>

      <BudgetAdjustmentModal
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        periodId={period.id}
        companyId={companyId}
        createdBy={createdBy}
        periodSubtitle={`${periodLabel(period.cadence_type, period.period_start)} · ${dollarsFromCents(totals.spentCents)} spent of ${dollarsFromCents(capacityCents)}`}
      />
    </div>
  );
}

/**
 * Burn-rate warning row. Renders below the four stats. Severity drives
 * the visual treatment:
 *   - 'none'     → nothing (returns null)
 *   - 'caution'  → subtle gray caption ("On pace — you'll use about
 *                  your full budget")
 *   - 'warning'  → butter-yellow banner with overrun amount + exhaust
 *                  date + Settings link
 *   - 'critical' → coral banner with "Critical:" prefix + same payload
 *
 * Every banner (warning + critical) includes the Settings link as the
 * only call-to-action. Submit/checkout buttons elsewhere stay enabled —
 * the widget never blocks an action. Per MS552 §6: "Lembra is a
 * recognition product, not a procurement tool — blocking a birthday
 * gift is brand-damaging in a way blocking a vendor invoice is not."
 */
function BurnRateWarning({ forecast }: { forecast: BurnRateForecast }) {
  const { warningLevel, overrunCents, forecastExhaustDate } = forecast;
  if (warningLevel === "none") return null;

  if (warningLevel === "caution") {
    return (
      <div
        className="mt-3 text-[12px]"
        style={{ color: "rgba(29,52,97,0.55)" }}
        data-testid="burn-rate-caution"
      >
        On pace to use about your full budget by the end of the period.
      </div>
    );
  }

  // 'warning' and 'critical' share the banner shape — different palette
  // and copy prefix. Coral on critical matches the existing "warn" tone
  // used by the negative-remaining Stat; butter on warning is the
  // existing brand caution colour (Dashboard.tsx uses #FCEEC8 family
  // for soft notices).
  const isCritical = warningLevel === "critical";
  const exhaustCopy = forecastExhaustDate
    ? ` by ${format(parseISO(forecastExhaustDate), "d MMM")}`
    : "";
  const overrunCopy = dollarsFromCents(overrunCents);

  return (
    <div
      className="mt-3 rounded-md border p-3 text-[12.5px]"
      style={{
        background: isCritical ? "#FBE9E5" : "#FCF4DC",
        borderColor: isCritical ? "#E8A89D" : "#E4D29C",
        color: isCritical ? "#7A2F25" : "#5A4612",
      }}
      data-testid={
        isCritical ? "burn-rate-critical" : "burn-rate-warning"
      }
    >
      <div className="font-medium">
        {isCritical ? "Critical: " : ""}
        At current pace, you&rsquo;ll exceed your budget by {overrunCopy}
        {exhaustCopy}.
      </div>
      <div className="mt-1.5">
        <Link
          to="/settings/program#budget"
          className="underline underline-offset-2"
          style={{
            color: isCritical ? "#7A2F25" : "#5A4612",
          }}
          data-testid="burn-rate-settings-link"
        >
          Adjust your budget from Settings
        </Link>
      </div>
    </div>
  );
}

/**
 * Compact stat cell. Three of these sit in the widget body. Tone
 * "warn" turns the value coral when remaining is negative (the buyer
 * has overspent the period); MS558's burn-rate row will lean on the
 * same colour for forecast crossings.
 */
function Stat({
  label,
  cents,
  tone = "default",
}: {
  label: string;
  cents: number;
  tone?: "default" | "warn";
}) {
  return (
    <div>
      <div
        className="text-[10px] font-medium uppercase"
        style={{
          fontFamily: "JetBrains Mono, monospace",
          letterSpacing: "0.06em",
          color: "rgba(29,52,97,0.55)",
        }}
      >
        {label}
      </div>
      <div
        className="mt-1 text-[18px] font-semibold tabular-nums"
        style={{
          color: tone === "warn" ? "#C4564A" : "rgba(29,52,97,0.92)",
        }}
      >
        {dollarsFromCents(cents)}
      </div>
    </div>
  );
}

function PoolWidgetSkeleton() {
  return (
    <div
      className="mx-8 mb-4 rounded-lg border bg-white p-5"
      style={{ borderColor: "rgba(29,52,97,0.08)" }}
      data-testid="dashboard-pool-widget-loading"
    >
      <div className="mb-3 h-4 w-32 animate-pulse rounded"
        style={{ background: "rgba(29,52,97,0.06)" }} />
      <div className="mb-4 h-1.5 w-full animate-pulse rounded-full"
        style={{ background: "rgba(29,52,97,0.06)" }} />
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i}>
            <div className="mb-2 h-3 w-16 animate-pulse rounded"
              style={{ background: "rgba(29,52,97,0.06)" }} />
            <div className="h-5 w-20 animate-pulse rounded"
              style={{ background: "rgba(29,52,97,0.06)" }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Format cents as a whole-dollar AUD string ("$1,860"). Mirrors
 * ApprovalModal.tsx's dollars() helper — kept inline here rather than
 * imported because that helper lives inside a component file and we
 * don't want to re-export it just for this surface. Negative cents
 * render with a "-" prefix (e.g. "-$120") for the over-budget case.
 */
function dollarsFromCents(cents: number): string {
  const abs = Math.abs(cents);
  const formatted = `$${(abs / 100).toLocaleString("en-AU", {
    maximumFractionDigits: 0,
  })}`;
  return cents < 0 ? `-${formatted}` : formatted;
}

/**
 * Cadence-aware label like "Monthly · May 2026" / "Quarterly · Q2 2026"
 * / "Weekly · 12 May". Period start is a YYYY-MM-DD string so we parse
 * deterministically. Quarter math: Q1 = months 1–3 etc.
 */
export function periodLabel(
  cadence: "weekly" | "monthly" | "quarterly" | "per_celebration",
  periodStart: string,
): string {
  // per_celebration shouldn't reach here (widget hides earlier), but
  // guard so a stray render doesn't crash on the cadence branch.
  if (cadence === "per_celebration") return "";
  const start = parseISO(periodStart);
  if (cadence === "monthly") {
    return `Monthly · ${format(start, "MMMM yyyy")}`;
  }
  if (cadence === "quarterly") {
    const month = start.getMonth(); // 0-indexed
    const quarter = Math.floor(month / 3) + 1;
    return `Quarterly · Q${quarter} ${format(start, "yyyy")}`;
  }
  // weekly
  return `Weekly · ${format(start, "d MMM")}`;
}

/**
 * "Period ends in N days" copy. Uses the calendar-day diff between
 * today and period_end so the boundary feels natural ("ends today",
 * "ends tomorrow", "ends in 3 days"). Past dates render as "ended N
 * days ago" — this is mostly for tests; the widget only renders when
 * the period contains today, so the live path always returns the
 * forward case.
 */
export function periodEndsLabel(periodEnd: string): string {
  const end = parseISO(periodEnd);
  const today = new Date();
  // Strip time so the diff is calendar-day not duration-based.
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const days = differenceInCalendarDays(end, todayMidnight);
  if (days < 0) {
    const ago = Math.abs(days);
    return ago === 1 ? "Ended yesterday" : `Ended ${ago} days ago`;
  }
  if (days === 0) return "Period ends today";
  if (days === 1) return "Period ends tomorrow";
  return `Period ends in ${days} days`;
}

// periodLabel + periodEndsLabel are already exported above for the
// test surface — they're pure helpers the widget tests pin directly
// without rendering the whole component. dollarsFromCents stays
// module-private (assertable indirectly via the rendered DOM).
